import React, { useEffect, useState, useMemo, useRef } from "react";
import { Card, CardBody, Col, Container, Row, Button, Input, Label } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import SearchableSelect from "../../../Components/Common/SearchableSelect";
import FilterCard from "../../../Components/Common/FilterCard";
import { DataTable, Column, TableAction } from "../../../Components/Common/DataTable";
import { showModal, hideModal, showConfirmModal } from "../../../hooks/useModal";
import AlerteDetailContent from "../AlerteDetail/AlerteDetailContent";
import AffectationEquipeModal from "./AffectationEquipeModal";
import { useLayoutStore } from "../../../stores/useLayoutStore";
import { useAlertesListFiltersStore, getDefaultDateRange } from "../../../stores/useAlertesListFiltersStore";
import { useFilterData } from "./filter-hook";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAlertesQuery,
  type AlertesQueryResult,
  useValiderAlerteMutation,
  useCommunesQuery,
  usePrioritesQuery,
  useTypeTravauxQuery,
  useTypeChausseQuery,
  useTypeAlerteQuery,
  useDegradationsQuery,
  useNiveauGraviteQuery,
  useDeleteAlerteMutation,
  invalidateAlertesQueries,
} from "../../../hooks/queries/useRoadAlertQueries";
import { toastSuccess, toastError } from "../../../utils/toast";
import { AlerteDetail } from "../AlerteDetail/types";
import { useAuthStore } from "../../../stores/useAuthStore";
import { createAlerte, getAlertesByCriteria } from "../../../services/request-api";

interface CreateAlerteModalProps {
  idStatutAlerte?: string;
  onSuccess: () => void;
}

function CreateAlerteModal({ idStatutAlerte, onSuccess }: CreateAlerteModalProps) {
  const user = useAuthStore((s) => s.user);

  const { data: communes = [] } = useCommunesQuery();
  const { data: priorites = [] } = usePrioritesQuery();
  const { data: typesTravaux = [] } = useTypeTravauxQuery();
  const { data: typesChausse = [] } = useTypeChausseQuery();
  const { data: typesAlerte = [] } = useTypeAlerteQuery();
  const { data: degradationsList = [] } = useDegradationsQuery();
  const { data: niveauxGravite = [] } = useNiveauGraviteQuery();

  const [libelle, setLibelle] = useState("");
  const [description, setDescription] = useState("");
  const [quartier, setQuartier] = useState("");
  const [idCommune, setIdCommune] = useState<string>("");
  const [idTypeAlerte, setIdTypeAlerte] = useState<string>("");
  const [nomVoie, setNomVoie] = useState("");
  const [idPriorite, setIdPriorite] = useState<string>("");
  const [idTypeChausse, setIdTypeChausse] = useState<string>("");
  const [idTypeTravaux, setIdTypeTravaux] = useState<string>("");
  const [longitude, setLongitude] = useState("");
  const [latitude, setLatitude] = useState("");
  const [surface, setSurface] = useState("");
  const [longueur, setLongueur] = useState("");
  const [largeur, setLargeur] = useState("");
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [degradations, setDegradations] = useState<{ idDegradation: string; idNiveauGravite: string }[]>([
    { idDegradation: "", idNiveauGravite: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const submittingRef = useRef(false);

  const STEPS = [
    { id: 1, label: "Informations générales", icon: "ri-file-text-line" },
    { id: 2, label: "Détails & localisation", icon: "ri-map-pin-line" },
    { id: 3, label: "Dégradations", icon: "ri-alert-line" },
    { id: 4, label: "Photos du site", icon: "ri-image-line" },
  ];

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(",") ? result.split(",")[1] : result;
        resolve(base64 || "");
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const communeOptions = (communes as any[]).map((c: any) => ({
    value: String(c.idCommune ?? c.id ?? ""),
    label: c.libelleCommune ?? c.libelle ?? "-",
  }));

  const prioriteOptions = (priorites as any[]).map((p: any) => ({
    value: String(p.idPriorite ?? p.id ?? ""),
    label: p.libellePriorite ?? p.libelle ?? "-",
  }));

  const typeTravauxOptions = (typesTravaux as any[]).map((t: any) => ({
    value: String(t.idTypeTravaux ?? t.id ?? ""),
    label: t.libelleTypeTravaux ?? t.libelle ?? "-",
  }));

  const typeChausseOptions = (typesChausse as any[]).map((t: any) => ({
    value: String(t.idTypeChausse ?? t.id ?? ""),
    label: t.libelleTypeChausse ?? t.libelle ?? "-",
  }));

  const typeAlerteOptions = (typesAlerte as any[]).map((t: any) => ({
    value: String(t.idTypeAlerte ?? t.id ?? ""),
    label: t.libTypeAlerte ?? t.libelleTypeAlerte ?? t.libelle ?? "-",
  }));

  const degradationOptions = (degradationsList as any[]).map((d: any) => ({
    value: String(d.idDegradation ?? d.id ?? ""),
    label: d.libelleDegradation ?? d.libelle ?? "-",
  }));

  const graviteOptions = (niveauxGravite as any[]).map((g: any) => ({
    value: String(g.idNiveauGravite ?? g.id ?? ""),
    label: g.libelleNiveauGravite ?? g.libelle ?? "-",
  }));

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    const release = () => {
      submittingRef.current = false;
      setSubmitting(false);
    };

    if (!libelle.trim()) {
      toastError("Le libellé est obligatoire.");
      release();
      return;
    }
    if (!description.trim()) {
      toastError("La description est obligatoire.");
      release();
      return;
    }
    if (!idCommune.trim()) {
      toastError("La commune est obligatoire.");
      release();
      return;
    }
    if (!idTypeAlerte.trim()) {
      toastError("Le type d'alerte est obligatoire.");
      release();
      return;
    }
    if (!idPriorite.trim()) {
      toastError("La priorité est obligatoire.");
      release();
      return;
    }
    if (!idTypeChausse.trim()) {
      toastError("Le type de chaussée est obligatoire.");
      release();
      return;
    }
    if (!idTypeTravaux.trim()) {
      toastError("Le type de travaux est obligatoire.");
      release();
      return;
    }
    const surfaceVal = surface.trim();
    if (surfaceVal === "") {
      toastError("La surface (m²) est obligatoire.");
      release();
      return;
    }
    if (Number.isNaN(Number(surfaceVal)) || Number(surfaceVal) < 0) {
      toastError("La surface doit être un nombre positif ou 0.");
      release();
      return;
    }
    if (photoFiles.length === 0) {
      toastError("Les photos du site sont obligatoires. Ajoutez au moins une photo.");
      release();
      return;
    }
    const validDegradations = degradations.filter((d) => d.idDegradation.trim() && d.idNiveauGravite.trim());
    if (validDegradations.length === 0) {
      toastError("Veuillez saisir au moins une dégradation (type et niveau de gravité).");
      release();
      return;
    }

    const creePar = (user?.username as string) || (user?.name as string) || "admin";

    const dataFiles: { fileName: string; extension: string; fileBase64: string }[] = [];
    for (let i = 0; i < photoFiles.length; i++) {
      const file = photoFiles[i];
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const base64 = await fileToBase64(file);
      dataFiles.push({
        fileName: `photo_${i + 1}`,
        extension: ext,
        fileBase64: base64,
      });
    }

    const dataDegradation = validDegradations.map((d) => ({
      idDegradation: d.idDegradation.trim(),
      idNiveauGravite: d.idNiveauGravite.trim(),
      longueur: longueur.trim() || "0",
      largeur: largeur.trim() || "0",
      surface: surfaceVal,
    }));

    const payload = {
      data: {
        libelleAlerte: libelle.trim(),
        description: description.trim(),
        quartier: quartier.trim() || "-",
        idCommune: idCommune.trim(),
        idTypeAlerte: idTypeAlerte.trim(),
        nomVoie: nomVoie.trim(),
        codeMaintenance: "",
        siRoutePeage: "1",
        siPriseEnCharge: "1",
        siDejaExistant: "1",
        longitude: longitude.trim() || "0",
        latitude: latitude.trim() || "0",
        idPriorite: idPriorite.trim(),
        idTypeChausse: idTypeChausse.trim(),
        idTypeTravaux: idTypeTravaux.trim(),
        idChefEquipe: "",
        idStatutAlerte: idStatutAlerte?.trim() || "7",
        creePar,
        nbreDegradation: dataDegradation.length,
        surface: surfaceVal,
        longueur: longueur.trim() || "0",
        largeur: largeur.trim() || "0",
        dataFiles,
        dataDegradation,
      },
    };

    try {
      const res = await createAlerte(payload);
      if (res && typeof res === "object" && "success" in res && !res.success) {
        throw new Error(res.message || "Erreur lors de la création de l'alerte.");
      }
      toastSuccess("Alerte créée avec succès.");
      hideModal();
      onSuccess();
    } catch (err: any) {
      toastError(err?.message ?? "Erreur lors de la création de l'alerte.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const canGoNext = () => {
    if (step === 1) return !!libelle.trim() && !!idCommune && !!idTypeAlerte;
    if (step === 2) return true;
    if (step === 3) return degradations.filter((d) => (d.idDegradation || "").trim() && (d.idNiveauGravite || "").trim()).length > 0;
    if (step === 4) return photoFiles.length > 0;
    return false;
  };

  return (
    <div className="p-3">
      {/* Stepper */}
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between">
          {STEPS.map((s, idx) => (
            <div key={s.id} className="d-flex align-items-center flex-grow-1">
              <div
                className={`d-flex align-items-center justify-content-center rounded-circle ${step === s.id ? "bg-primary text-white" : step > s.id ? "bg-success text-white" : "bg-body-secondary text-body-secondary"}`}
                style={{ width: 36, height: 36, minWidth: 36 }}
              >
                {step > s.id ? <i className="ri-check-line" /> : <i className={s.icon} />}
              </div>
              <span className={`ms-2 small d-none d-md-inline ${step >= s.id ? "text-body-emphasis fw-medium" : "text-muted"}`}>
                {s.label}
              </span>
              {idx < STEPS.length - 1 && (
                <div className="flex-grow-1 mx-2" style={{ height: 2, backgroundColor: step > s.id ? "var(--bs-success)" : "var(--bs-light)" }} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Étape 1 : Informations générales */}
      {step === 1 && (
        <Row className="g-3">
          <Col md={12}>
            <Label>Libellé</Label>
            <Input
              value={libelle}
              onChange={(e) => setLibelle(e.target.value)}
              placeholder="Ex : Alerte chaussée dégradée"
            />
          </Col>
          <Col md={12}>
            <Label>Description</Label>
            <Input
              type="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Décrivez l'alerte..."
            />
          </Col>
          <Col md={6}>
            <Label>Commune</Label>
            <SearchableSelect
              options={communeOptions}
              value={idCommune || null}
              onChange={(value) => setIdCommune(value ? String(value) : "")}
              placeholder="Sélectionner une commune"
            />
          </Col>
          <Col md={6}>
            <Label>Quartier (optionnel)</Label>
            <Input
              value={quartier}
              onChange={(e) => setQuartier(e.target.value)}
              placeholder="Ex : Quartier, zone..."
            />
          </Col>
          <Col md={12}>
            <Label>Type d'alerte</Label>
            <SearchableSelect
              options={typeAlerteOptions}
              value={idTypeAlerte || null}
              onChange={(value) => setIdTypeAlerte(value ? String(value) : "")}
              placeholder="Sélectionner un type d'alerte"
            />
          </Col>
        </Row>
      )}

      {/* Étape 2 : Détails & localisation */}
      {step === 2 && (
        <Row className="g-3">
          <Col md={6}>
            <Label>Priorité</Label>
            <SearchableSelect
              options={prioriteOptions}
              value={idPriorite || null}
              onChange={(value) => setIdPriorite(value ? String(value) : "")}
              placeholder="Sélectionner une priorité"
            />
          </Col>
          <Col md={6}>
            <Label>Nom de la voie (optionnel)</Label>
            <Input
              value={nomVoie}
              onChange={(e) => setNomVoie(e.target.value)}
              placeholder="Ex : Boulevard Latrille"
            />
          </Col>
          <Col md={6}>
            <Label>Type de chaussée</Label>
            <SearchableSelect
              options={typeChausseOptions}
              value={idTypeChausse || null}
              onChange={(value) => setIdTypeChausse(value ? String(value) : "")}
              placeholder="Sélectionner un type de chaussée"
            />
          </Col>
          <Col md={6}>
            <Label>Type de travaux</Label>
            <SearchableSelect
              options={typeTravauxOptions}
              value={idTypeTravaux || null}
              onChange={(value) => setIdTypeTravaux(value ? String(value) : "")}
              placeholder="Sélectionner un type de travaux"
            />
          </Col>
          <Col md={4}>
            <Label>Surface (m²)</Label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={surface}
              onChange={(e) => setSurface(e.target.value)}
              placeholder="Ex : 90"
            />
          </Col>
          <Col md={4}>
            <Label>Longueur (m, optionnel)</Label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={longueur}
              onChange={(e) => setLongueur(e.target.value)}
              placeholder="Ex : 10"
            />
          </Col>
          <Col md={4}>
            <Label>Largeur (m, optionnel)</Label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={largeur}
              onChange={(e) => setLargeur(e.target.value)}
              placeholder="Ex : 9"
            />
          </Col>
          <Col md={6}>
            <Label>Longitude (optionnel)</Label>
            <Input
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              placeholder="Ex : -3.9876"
            />
          </Col>
          <Col md={6}>
            <Label>Latitude (optionnel)</Label>
            <Input
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              placeholder="Ex : 5.3210"
            />
          </Col>
        </Row>
      )}

      {/* Étape 3 : Dégradations */}
      {step === 3 && (
        <Row className="g-3">
          <Col md={12}>
            <Label>Dégradations (obligatoire)</Label>
            <p className="text-muted small mb-2">Ajoutez au moins une dégradation avec son niveau de gravité.</p>
            {degradations.map((deg, index) => (
              <Row key={index} className="g-2 mb-2 align-items-end">
                <Col md={5}>
                  <SearchableSelect
                    options={degradationOptions}
                    value={deg.idDegradation || null}
                    onChange={(value) => {
                      const next = [...degradations];
                      next[index] = { ...next[index], idDegradation: value ? String(value) : "" };
                      setDegradations(next);
                    }}
                    placeholder="Type de dégradation"
                  />
                </Col>
                <Col md={5}>
                  <SearchableSelect
                    options={graviteOptions}
                    value={deg.idNiveauGravite || null}
                    onChange={(value) => {
                      const next = [...degradations];
                      next[index] = { ...next[index], idNiveauGravite: value ? String(value) : "" };
                      setDegradations(next);
                    }}
                    placeholder="Niveau de gravité"
                  />
                </Col>
                <Col md={2}>
                  {degradations.length > 1 ? (
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      onClick={() => setDegradations(degradations.filter((_, i) => i !== index))}
                    >
                      <i className="ri-delete-bin-line" />
                    </Button>
                  ) : null}
                </Col>
              </Row>
            ))}
            <Button
              type="button"
              color="secondary"
              size="sm"
              outline
              onClick={() => setDegradations([...degradations, { idDegradation: "", idNiveauGravite: "" }])}
            >
              <i className="ri-add-line me-1" />
              Ajouter une dégradation
            </Button>
          </Col>
        </Row>
      )}

      {/* Étape 4 : Photos */}
      {step === 4 && (
        <Row className="g-3">
          <Col md={12}>
            <Label>Photos du site (obligatoire)</Label>
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  setPhotoFiles(Array.from(files));
                }
              }}
            />
            {photoFiles.length > 0 && (
              <small className="text-muted d-block mt-1">
                {photoFiles.length} photo(s) sélectionnée(s) : {photoFiles.map((f) => f.name).join(", ")}
              </small>
            )}
          </Col>
        </Row>
      )}

      {/* Pied : navigation */}
      <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center">
        <Button color="secondary" outline onClick={() => hideModal()} disabled={submitting}>
          Annuler
        </Button>
        <div className="d-flex gap-2 align-items-center">
          {submitting && (
            <span className="text-muted me-2">
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
              Création en cours...
            </span>
          )}
          {step > 1 && (
            <Button color="secondary" outline onClick={() => setStep((s) => s - 1)} disabled={submitting}>
              Précédent
            </Button>
          )}
          {step < 4 ? (
            <Button color="primary" onClick={() => setStep((s) => s + 1)} disabled={!canGoNext()}>
              Suivant
            </Button>
          ) : (
            <Button color="primary" onClick={handleSubmit} disabled={submitting || !canGoNext()}>
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                  Création...
                </>
              ) : (
                "Créer l'alerte"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface AlertesListProps {
  title: string;
  idStatutAlerte?: string;
}

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

/** Payload complet pour la requête alertes : data (filtres) + page + size au même niveau. */
type AlertesListPayload = {
  data: Record<string, unknown>;
  page: number;
  size: number;
};

// L'API attend une page 0-based (0 = première page). L'UI reste en 1-based.
const buildRequestData = (
  filters: {
    idCommune: string;
    idPriorite: string;
    dateDebut: string;
    dateFin: string;
  },
  idStatutAlerte?: string
): Record<string, unknown> => {
  const requestData: Record<string, unknown> = {
    idCommune: filters.idCommune || "",
    traitePar: "",
    creePar: "",
    idPriorite: filters.idPriorite || "",
    idStatutAlerte: idStatutAlerte || "",
  };
  if (filters.dateDebut && filters.dateFin) {
    requestData.dateCreationParam = {
      operator: "[]",
      start: filters.dateDebut,
      end: filters.dateFin,
    };
  }
  return requestData;
};

const buildPayload = (
  filters: { idCommune: string; idPriorite: string; dateDebut: string; dateFin: string },
  idStatutAlerte: string | undefined,
  page: number,
  size: number
): AlertesListPayload => ({
  data: buildRequestData(filters, idStatutAlerte),
  page: Math.max(0, page - 1),
  size,
});

const AlertesList = ({ title, idStatutAlerte }: AlertesListProps) => {
  const queryClient = useQueryClient();
  const layoutModeType = useLayoutStore((s) => s.layoutModeType);
  const isDarkMode = layoutModeType === 'dark';
  const dateDebut = useAlertesListFiltersStore((s) => s.dateDebut);
  const dateFin = useAlertesListFiltersStore((s) => s.dateFin);
  const setDateDebut = useAlertesListFiltersStore((s) => s.setDateDebut);
  const setDateFin = useAlertesListFiltersStore((s) => s.setDateFin);
  const resetDateRange = useAlertesListFiltersStore((s) => s.resetDateRange);

  const [filters, setFilters] = useState({
    idCommune: "",
    idPriorite: "",
    idStatutAlerte: idStatutAlerte || "",
    traitePar: "",
    creePar: ""
  });
  const [selectedAlertes, setSelectedAlertes] = useState<AlerteDetail[]>([]);
  const [tableKey, setTableKey] = useState(0);
  const [searchParams, setSearchParams] = useState<AlertesListPayload | null>(null);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_SIZE);
  const initialLoadDone = useRef(false);

  const filtersWithDates = useMemo(
    () => ({ ...filters, dateDebut, dateFin }),
    [filters, dateDebut, dateFin]
  );

  const { options, loading: filtersLoading } = useFilterData();
  const requestPayload = searchParams ?? buildPayload(filtersWithDates, idStatutAlerte, page, pageSize);
  const alertesQuery = useAlertesQuery(
    requestPayload,
    { enabled: !filtersLoading && searchParams !== null }
  );
  const queryResult = alertesQuery.data as AlertesQueryResult | undefined;
  const totalCount = queryResult?.total ?? 0;
  const alertes = useMemo(() => {
    const rawItems = queryResult?.items ?? [];
    const rawAlertes = (Array.isArray(rawItems) ? rawItems : []) as AlerteDetail[];
    const seen = new Set<number>();
    return rawAlertes.filter((row) => {
      const id = row?.idAlerte;
      if (id == null || Number.isNaN(Number(id))) return true;
      const numId = Number(id);
      if (seen.has(numId)) return false;
      seen.add(numId);
      return true;
    });
  }, [queryResult]);
  const loading = alertesQuery.isLoading;
  const validerMutation = useValiderAlerteMutation();
  const deleteMutation = useDeleteAlerteMutation();

  useEffect(() => {
    if (!filtersLoading && !initialLoadDone.current) {
      initialLoadDone.current = true;
      setPage(1);
      setSearchParams(buildPayload(filtersWithDates, idStatutAlerte, 1, pageSize));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only when filters ready
  }, [filtersLoading, idStatutAlerte]);

  useEffect(() => {
    if (initialLoadDone.current) {
      setPage(1);
      setSearchParams(buildPayload(filtersWithDates, idStatutAlerte, 1, pageSize));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when route (idStatutAlerte) changes
  }, [idStatutAlerte]);

  const loadData = () => {
    setPage(1);
    setSearchParams(buildPayload(filtersWithDates, idStatutAlerte, 1, pageSize));
  };

  // Préchargement des pages suivantes
  useEffect(() => {
    if (searchParams) {
      const prefetchPage = (p: number) => {
        const payload = { ...searchParams, page: Math.max(0, p - 1) };
        queryClient.prefetchQuery({
          queryKey: ["alertes", { data: payload, page: payload.page, size: payload.size }],
          // On utilise la query par défaut qui sera déclenchée si besoin
          queryFn: () => getAlertesByCriteria({ data: payload }),
          staleTime: 5 * 60 * 1000,
        });
      };
      if (page < 100) {
        prefetchPage(page + 1);
        prefetchPage(page + 2);
      }
    }
  }, [page, searchParams, queryClient]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    if (newPageSize !== pageSize) setPageSize(newPageSize);
    setSearchParams(buildPayload(filtersWithDates, idStatutAlerte, newPage, newPageSize));
    setSelectedAlertes([]); // Reset selection on page change
  };

  const handleBulkAffectation = () => {
    if (selectedAlertes.length === 0) return;

    showModal({
      title: "Affectation groupée d'alertes",
      size: "md",
      content: (
        <AffectationEquipeModal
          alertes={selectedAlertes}
          onSuccess={() => {
            setSelectedAlertes([]);
            setTableKey(prev => prev + 1);
            loadData();
          }}
        />
      ),
      closeOnBackdropClick: false,
      haveDefaultActionsButton: false,
    });
  };

  // Configuration des colonnes
  const columns: Column<AlerteDetail>[] = useMemo(() => [
    {
      key: "idAlerte",
      label: "ID",
      sortable: true,
      visible: true,
      render: (_: unknown, row) => (
        <span className="fw-medium" style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>
          #{row?.idAlerte ?? "-"}
        </span>
      ),
    },
    {
      key: "description",
      label: "Description",
      sortable: true,
      visible: true,
      render: (_: unknown, row) => (
        <span className="fw-semibold" style={{ color: isDarkMode ? '#fff' : '#212529' }}>
          {row?.description ?? row?.libelleAlerte ?? "-"}
        </span>
      ),
    },
    {
      key: "typeTravaux",
      label: "Types travaux",
      sortable: true,
      visible: true,
      render: (_: unknown, row) => (
        <span style={{ color: isDarkMode ? '#adb5bd' : '#495057' }}>
          {row?.typeTravaux?.libelleTypeTravaux ?? row?.libelleTypeTravaux ?? "-"}
        </span>
      ),
    },
    {
      key: "priorite",
      label: "Priorité",
      sortable: true,
      visible: true,
      render: (_: unknown, row) => {
        const prioriteLibelle = row?.priorite?.libellePriorite || row?.libellePriorite || "-";
        const rawId = (row?.priorite as { id?: string; idPriorite?: number })?.idPriorite ?? row?.priorite?.idPriorite;
        const prioriteId = rawId != null ? Number(rawId) : null;
        const prioriteLibelleStr = String(prioriteLibelle).toUpperCase();

        // Couleurs selon les IDs API : 3=TRES URGENT, 2=URGENT, 1=NORMAL, 0=NON DEFINI
        const getPrioriteBadgeClass = () => {
          if (prioriteId !== null && !Number.isNaN(prioriteId)) {
            if (prioriteId === 3) return "bg-danger";           // TRES URGENT
            if (prioriteId === 2) return "bg-warning text-dark"; // URGENT
            if (prioriteId === 1) return "bg-info";            // NORMAL
            if (prioriteId === 0) return "bg-secondary";       // NON DEFINI
          }
          if (prioriteLibelleStr.includes("TRES URGENT")) return "bg-danger";
          if (prioriteLibelleStr.includes("URGENT")) return "bg-warning text-dark";
          if (prioriteLibelleStr.includes("NORMAL")) return "bg-info";
          if (prioriteLibelleStr.includes("NON DEFINI")) return "bg-secondary";
          return "bg-secondary";
        };

        return (
          <span className={`badge ${getPrioriteBadgeClass()} px-2 py-2 rounded-pill fw-medium`} style={{ fontSize: "0.8rem" }}>
            {prioriteLibelle}
          </span>
        );
      },
    },
    {
      key: "communes",
      label: "Localisation",
      sortable: true,
      visible: true,
      render: (_: unknown, row) => (
        <span style={{ color: isDarkMode ? '#adb5bd' : '#495057' }}>
          {row?.communes?.libelleCommune || "-"}
        </span>
      ),
    },
    {
      key: "dateCreation",
      label: "Date création",
      sortable: true,
      visible: true,
      render: (value: string) => {
        if (!value) return <span style={{ color: isDarkMode ? '#6c757d' : '#6c757d' }}>-</span>;
        try {
          return (
            <span style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>
              {new Date(value).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })}
            </span>
          );
        } catch {
          return <span style={{ color: isDarkMode ? '#6c757d' : '#6c757d' }}>-</span>;
        }
      },
    },
  ], [isDarkMode]);

  // Actions dynamiques (Affecter sur Grand public et Non affectées)
  const actions: TableAction<AlerteDetail>[] = useMemo(() => {
    const list: TableAction<AlerteDetail>[] = [
      {
        label: "Voir les détails",
        icon: "ri-eye-line",
        onClick: (row) => {
          showModal({
            title: `Détails de l'alerte - ${row.libelleAlerte || 'N/A'}`,
            size: "xl",
            content: (
              <AlerteDetailContent
                alerteId={row.idAlerte}
                alerte={row}
              />
            ),
            closeOnBackdropClick: false,
            haveDefaultActionsButton: false,
          });
        },
        variant: "primary"
      },
    ];
    if (idStatutAlerte === "1" || idStatutAlerte === "7") {
      list.push({
        label: "Affecter",
        icon: "ri-user-add-line",
        onClick: (row) => {
          showModal({
            title: "Affecter l'alerte à une équipe",
            size: "md",
            content: (
              <AffectationEquipeModal
                alerte={row}
                onSuccess={() => {
                  hideModal();
                  setSelectedAlertes([]);
                  setTableKey(prev => prev + 1);
                  loadData();
                }}
              />
            ),
            closeOnBackdropClick: false,
            haveDefaultActionsButton: false,
          });
        },
        variant: "success"
      });
    }
    if (idStatutAlerte === "2") {
      list.push({
        label: "Valider",
        icon: "ri-checkbox-circle-line",
        onClick: (row) => {
          showConfirmModal({
            title: "Valider l'alerte",
            message: `Voulez-vous valider l'alerte « ${row.libelleAlerte ?? row.description ?? `#${row.idAlerte}`} » et la passer en cours ?`,
            confirmText: "Valider",
            cancelText: "Annuler",
            confirmVariant: "primary",
            type: "info",
            onConfirm: () => {
              validerMutation.mutate(row.idAlerte, {
                onSuccess: () => {
                  toastSuccess("L'alerte a été validée.");
                  loadData();
                },
                onError: (err: any) => {
                  toastError(err?.message ?? "Erreur lors de la validation.");
                },
              });
            },
          });
        },
        variant: "success"
      });
    }
    if (idStatutAlerte === "1" || idStatutAlerte === "7") {
      list.push({
        label: "Supprimer",
        icon: "ri-delete-bin-line",
        variant: "danger",
        onClick: (row) => {
          showConfirmModal({
            title: "Supprimer l'alerte",
            message: `Êtes-vous sûr de vouloir supprimer l'alerte « ${row.libelleAlerte ?? row.description ?? `#${row.idAlerte}`} » ? Cette action est irréversible.`,
            confirmText: "Oui, supprimer",
            cancelText: "Annuler",
            confirmVariant: "danger",
            type: "delete",
            onConfirm: () => {
              deleteMutation.mutate(row.idAlerte, {
                onSuccess: () => {
                  toastSuccess("L'alerte a été supprimée avec succès.");
                  loadData();
                },
                onError: (err: any) => {
                  toastError(err?.message ?? "Erreur lors de la suppression.");
                },
              });
            },
          });
        },
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadData is stable enough
  }, [idStatutAlerte]);

  document.title = `${title} | Road Alert AGEROUTE`;

  const hasActiveFilters = filters.idCommune || filters.idPriorite || dateDebut || dateFin;

  /** Création d'alerte autorisée uniquement sur Grand public (7) et Non affectées (1). */
  const canCreateAlerte = idStatutAlerte === "1" || idStatutAlerte === "7";
  const createAlerteModalTitle =
    idStatutAlerte === "7"
      ? "Créer une alerte routière - Grand public"
      : idStatutAlerte === "1"
        ? "Créer une alerte routière - Non affectées"
        : "Créer une alerte routière";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={title} pageTitle="Alertes" />

          {/* En-tête avec titre et action */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
            <div>
              <h5 className="mb-1 fw-semibold" style={{ color: isDarkMode ? "#fff" : "#212529" }}>
                {title}
              </h5>
              <p className="text-muted small mb-0">
                {alertes.length} alerte{alertes.length !== 1 ? "s" : ""} affichée{alertes.length !== 1 ? "s" : ""}
              </p>
            </div>
            {canCreateAlerte && (
              <Button
                className="btn-ageroute-orange rounded-pill px-4 shadow-sm"
                onClick={() => {
                  showModal({
                    title: createAlerteModalTitle,
                    size: "lg",
                    content: (
                      <CreateAlerteModal
                        idStatutAlerte={idStatutAlerte}
                        onSuccess={() => {
                          invalidateAlertesQueries(queryClient);
                          setPage(1);
                          setSearchParams(buildPayload(filtersWithDates, idStatutAlerte, 1, pageSize));
                        }}
                      />
                    ),
                    haveDefaultActionsButton: false,
                    closeOnBackdropClick: true,
                  });
                }}
              >
                <i className="ri-add-line me-2" />
                Créer une alerte
              </Button>
            )}
          </div>

          {/* Filtres */}
          <FilterCard title="Filtres de recherche">
            <Row className="g-3 align-items-end">
                <Col md={2}>
                  <Label className="small text-muted mb-1">
                    <i className="ri-map-pin-line me-1" />
                    Commune
                  </Label>
                  {loading ? (
                    <div className="d-flex align-items-center">
                      <span className="text-muted">Chargement...</span>
                    </div>
                  ) : (
                    <SearchableSelect
                      options={options.communeOptions}
                      value={filters.idCommune || null}
                      onChange={(value) => {
                        const newValue = value !== null && value !== undefined ? String(value) : "";
                        setFilters({ ...filters, idCommune: newValue });
                      }}
                      placeholder="--Tous--"
                    />
                  )}
                </Col>
                <Col md={2}>
                  <Label className="small text-muted mb-1">
                    <i className="ri-flag-line me-1" />
                    Priorité
                  </Label>
                  {loading ? (
                    <div className="d-flex align-items-center">
                      <span className="text-muted">Chargement...</span>
                    </div>
                  ) : (
                    <SearchableSelect
                      options={options.prioriteOptions}
                      value={filters.idPriorite || null}
                      onChange={(value) => {
                        const newValue = value !== null && value !== undefined ? String(value) : "";
                        setFilters({ ...filters, idPriorite: newValue });
                      }}
                      placeholder="--Tous--"
                    />
                  )}
                </Col>
                <Col md={2}>
                  <Label className="small text-muted mb-1">
                    <i className="ri-calendar-line me-1" />
                    Date début
                  </Label>
                  <Input
                    type="date"
                    className="form-control rounded-3"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Label className="small text-muted mb-1">
                    <i className="ri-calendar-line me-1" />
                    Date fin
                  </Label>
                  <Input
                    type="date"
                    className="form-control rounded-3"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Button
                    className="w-100 btn-ageroute-orange rounded-pill shadow-sm"
                    onClick={loadData}
                  >
                    <i className="ri-search-line me-1" />
                    Rechercher
                  </Button>
                </Col>
                {hasActiveFilters && (
                  <Col md={2}>
                    <Button
                      className="w-100 rounded-pill"
                      color="secondary"
                      outline
                      onClick={() => {
                        setFilters({ ...filters, idCommune: "", idPriorite: "" });
                        resetDateRange();
                        setPage(1);
                        setSearchParams(
                          buildPayload(
                            { ...filters, idCommune: "", idPriorite: "", ...getDefaultDateRange() },
                            idStatutAlerte,
                            1,
                            pageSize
                          )
                        );
                      }}
                    >
                      <i className="ri-refresh-line me-1" />
                      Réinitialiser
                    </Button>
                  </Col>
                )}
              </Row>
          </FilterCard>

          {/* Tableau des alertes */}
          <Card className="card-ageroute rounded-3 shadow-sm border-0 overflow-hidden">
            <CardBody className="p-3 p-md-4">
              <DataTable
                instanceId={`alertes-list-${idStatutAlerte || 'all'}`}
                key={`${idStatutAlerte}-${tableKey}`}
                columns={columns}
                data={alertes}
                isLoading={loading}
                exportExcel={false}
                exportPDF={false}
                actions={actions}
                actionsColumnLabel="Actions"
                emptyState={
                  <div className="text-center py-5 py-md-5 px-3">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: 80,
                        height: 80,
                        background: isDarkMode ? "rgba(255,109,0,0.15)" : "rgba(255,109,0,0.1)",
                      }}
                    >
                      <i
                        className="ri-inbox-line"
                        style={{ fontSize: "2.5rem", color: "var(--bs-primary)" }}
                      />
                    </div>
                    <h5 className="fw-semibold mb-2" style={{ color: isDarkMode ? "#fff" : "#212529" }}>
                      {hasActiveFilters ? "Aucune alerte ne correspond aux filtres" : "Aucune alerte trouvée"}
                    </h5>
                    <p className="text-muted mb-4 mx-auto" style={{ maxWidth: 360 }}>
                      {hasActiveFilters
                        ? "Modifiez vos critères de recherche ou réinitialisez les filtres."
                        : "Il n'y a actuellement aucune alerte dans cette catégorie."}
                    </p>
                    {hasActiveFilters && (
                      <Button
                        className="rounded-pill"
                        color="secondary"
                        outline
                        onClick={() => {
                          setFilters({ ...filters, idCommune: "", idPriorite: "" });
                          resetDateRange();
                          setPage(1);
                          setSearchParams(
                            buildPayload(
                              { ...filters, idCommune: "", idPriorite: "", ...getDefaultDateRange() },
                              idStatutAlerte,
                              1,
                              pageSize
                            )
                          );
                        }}
                      >
                        <i className="ri-refresh-line me-2" />
                        Réinitialiser les filtres
                      </Button>
                    )}
                  </div>
                }
                pageSize={pageSize}
                pageSizeOptions={[10, 20, 50, 100]}
                searchable={true}
                pagination={true}
                serverSidePagination={true}
                totalCount={totalCount}
                page={page}
                onPageChange={handlePageChange}
                selectable={canCreateAlerte}
                bulkActions={[
                  {
                    label: "Affecter à une équipe",
                    icon: "ri-user-add-line",
                    onClick: handleBulkAffectation,
                  }
                ]}
                onClearSelection={() => setSelectedAlertes([])}
                onSelectionChange={(rows) => setSelectedAlertes(rows)}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AlertesList;
